import React, { useState, useMemo } from 'react';
import { TableContent } from '../LayoutPage';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { Button, KIND, SIZE } from 'baseui/button';
import { Input } from 'baseui/input';
import { Filter, Search } from 'lucide-react';
import { MantineDataTable } from '../../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useAccessSettings, isPopulatedAccessSetting, AccessSetting } from '../../hooks/useAccessSettings';
import { AccessSettingDrawer } from '../../components/AccessSettingDrawer';
import { AddAccessSettingDrawer } from '../../components/AddAccessSettingDrawer';
import { useFdCan } from '../../hooks/useFdCan';

const PageContainer = styled('div', {
  padding: '24px',
  backgroundColor: '#fff',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px',
});

const FiltersContainer = styled('div', {
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '24px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
});

const TableContainer = styled('div', {
  flex: 1,
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  padding: '20px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

export const AccessSettings = () => {
  const canCreate = useFdCan('events', 'create');
  const { data: accessSettings, isLoading } = useAccessSettings();

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Drawer states
  const [selectedSetting, setSelectedSetting] = useState<AccessSetting | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  
  const handleRowClick = (setting: AccessSetting) => {
    setSelectedSetting(setting);
    setIsDrawerOpen(true);
  };
  
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedSetting(null);
  };

  // Filter and search logic
  const filteredSettings = useMemo(() => {
    if (!accessSettings) return [];

    let filtered = accessSettings.filter(isPopulatedAccessSetting);

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((setting) => {
        // Search in ID
        if (setting.id.toString().includes(query)) return true;
        
        // Search in configuration text
        if (setting.settings?.event?.[0]) {
          const config = setting.settings.event[0];
          return (
            config.title_en?.toLowerCase().includes(query) ||
            config.title_es?.toLowerCase().includes(query) ||
            config.short_name?.toLowerCase().includes(query)
          );
        }
        
        return false;
      });
    }

    return filtered;
  }, [accessSettings, searchQuery]);

  // Table columns
  const columns = useMemo<MRT_ColumnDef<AccessSetting>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
        Cell: ({ row }) => {
          const setting = row.original;
          return (
            <div style={{ fontSize: '13px', fontWeight: 600 }}>
              {setting.name || 'Unnamed'}
            </div>
          );
        },
      },
      {
        id: 'configuration',
        header: 'Configuration',
        size: 400,
        Cell: ({ row }) => {
          const config = row.original.settings?.event?.[0];

          // adults/child/categories are all optional — render whichever
          // sections exist instead of flagging the record as invalid.
          const parts: string[] = [];
          if (config?.adults) parts.push(`Adults: ${config.adults.min ?? 0}-${config.adults.max ?? 0}`);
          if (config?.child) parts.push(`Children: ${config.child.min_num ?? 0}-${config.child.max_num ?? 0}`);
          (config?.categories || []).forEach((c) =>
            parts.push(`${c.title_en || c.key}: ${c.min ?? 0}-${c.max ?? 0}`)
          );

          if (!config || parts.length === 0) {
            return <span style={{ color: '#9CA3AF' }}>No configuration</span>;
          }

          return (
            <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
              <strong>{config.title_en || 'Untitled'}</strong>
              <div style={{ color: '#6B7280', marginTop: '4px' }}>
                {parts.join(' | ')}
              </div>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <PageContainer>
      <Header>
        <HeadingMedium margin={0}>Access Settings</HeadingMedium>
        <div style={{ display: 'flex', gap: '12px' }}>
          {canCreate && (
          <Button
            size={SIZE.compact}
            onClick={() => setIsAddDrawerOpen(true)}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: 'var(--tenant-primary, #000)',
                  color: '#fff',
                  ':hover': {
                    backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #000) 85%, black)',
                  },
                },
              },
            }}
          >
            New Access Setting
          </Button>
          )}
          <Button
            size={SIZE.compact}
            kind={KIND.secondary}
            startEnhancer={() => <Filter size={16} />}
            onClick={() => setShowFilters(!showFilters)}
            overrides={{
              BaseButton: {
                style: {
                  backgroundColor: showFilters ? 'var(--tenant-primary, #000)' : '#fff',
                  color: showFilters ? '#fff' : '#000',
                  ':hover': { backgroundColor: showFilters ? 'color-mix(in srgb, var(--tenant-primary, #000) 85%, black)' : '#E5E7EB' },
                },
              },
            }}
          >
            Filters
          </Button>
        </div>
      </Header>

      {showFilters && (
        <FiltersContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Search size={20} color="#6B7280" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              placeholder="Search by name or description..."
              clearable
              overrides={{
                Root: {
                  style: {
                    flex: 1,
                  },
                },
              }}
            />
          </div>
        </FiltersContainer>
      )}

      <TableContainer>
        <MantineDataTable
          columns={columns}
          data={filteredSettings}
          loading={isLoading}
          enablePagination
          enableSorting
          enableGlobalFilter={false}
          initialPageSize={20}
          pageSizeOptions={[10, 20, 50, 100]}
          onRowClick={handleRowClick}
        />
      </TableContainer>
      
      <AccessSettingDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        setting={selectedSetting}
      />
      
      <AddAccessSettingDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
      />
    </PageContainer>
  );
};
